import {BannerDataJson} from '@core-client/ads/banner/data/BannerDataJson';
import {IBannerData} from '@core-client/ads/banner/data/IBannerData';
import type {IRefreshTrigger} from '../trigger/IRefreshTrigger';
import {LoggingTrigger} from '../trigger/LoggingTrigger';
import {getBannerStatLogger, getErrorOperationWithSuffix} from '../util/BannerStat';
import {Duration} from '../util/Duration';
import {BannerView} from '../view/BannerView';
import {BannerModel} from '../BannerModel';
import type {BannerRequest, IResponseListener} from '../BannerRequest';

import {IBanner} from './IBanner';

export interface ISimpleBannerOptions {
    view: BannerView | HTMLElement;
    model: BannerModel;
    /**
     * Надо ли при скрытии баннера скрывать содержащий его элемент-контейнер.
     */
    hideContainerOnHide?: boolean;
    onAfterHandleResponse?: (banner: IBannerData) => void;
    onBeforeHandleResponse?: (banner: IBannerData) => void;
    onBannerClick?: (banner: IBannerData) => unknown;
}

export class SimpleBanner implements IBanner {
    options: ISimpleBannerOptions;
    model: BannerModel;
    view: BannerView;
    request: BannerRequest | null = null;
    visible = false;
    inLayer = false;
    banner: IBannerData | null = null;
    durationSinceLastRefresh: Duration;
    triggers: Array<IRefreshTrigger>;
    listener: IResponseListener;

    constructor(options: ISimpleBannerOptions) {
        let {view, model} = options;

        this.options = options;
        this.model = model;

        if (view instanceof HTMLElement) {
            view = new BannerView({
                container: view,
                slot: model.slot,
                adfTransport: false
            });
        }

        this.view = view;
        this.durationSinceLastRefresh = new Duration();
        this.triggers = [];
        this.listener = {
            handleResponse: (banner: IBannerData) => {
                this.handleResponse(banner);
            }
        };
    }

    public setInLayer(inLayer: boolean): SimpleBanner {
        this.inLayer = inLayer;
        return this;
    }

    public isInViewport(): boolean {
        return this.view.isInViewport();
    }

    attachRefreshTrigger(trigger: IRefreshTrigger, place: string) {
        trigger.attach(this);
        this.triggers.push(new LoggingTrigger(trigger, place, this.view.getSlot()));
    }

    init(visible: boolean) {
        if (visible) {
            this.view.showContainer();
        } else {
            this.view.hideContainer();
        }
        this.triggers.forEach((trigger) => {
            trigger.init(visible);
        });
    }

    destroy() {
        this.hide();
        this.triggers.forEach((trigger) => {
            trigger.destroy();
        });
    }

    show() {
        let step = 0;
        const stepFirst = 1;
        const stepSecond = 2;
        try {
            if (!this.visible) {
                this.visible = true;
                this.view.attach(this);
                step = stepFirst;
                this.requestBanner();
                step = stepSecond;
                this.triggers.forEach((trigger) => {
                    trigger.show();
                });
            }
        } catch (ex) {
            getBannerStatLogger().errorEx(
                ex as Error,
                "SimpleBanner",
                "show_" + step,
                getErrorOperationWithSuffix(this.view.getSlot())
            );
            throw ex;
        }
    }

    hide() {
        if (this.options.hideContainerOnHide) {
            this.view.hideContainer();
        }
        if (this.visible) {
            this.triggers.forEach((trigger) => {
                trigger.hide();
            });
            this.visible = false;
            this.cancelBanner();
            this.view.detach();
        } else {
            this.view.renderNullBanner();
        }
    }

    protected cancelBanner() {
        if (this.request !== null) {
            this.request.cancel();
            this.request = null;
        }
    }

    requestBanner() {
        if (this.visible) {
            this.cancelBanner();
            this.request = this.model.request(this.view.getContainer(), this.listener);
            this.durationSinceLastRefresh = new Duration();
        }
    }

    getDurationSinceLastRefresh(): Duration {
        return this.durationSinceLastRefresh;
    }

    protected handleResponse(banner: IBannerData) {
        const { options } = this;
        if (options.onBeforeHandleResponse) {
            options.onBeforeHandleResponse(banner);
        }
        this.banner = banner;
        this.view.renderBanner(banner);
        this.request = null;
        if (options.onAfterHandleResponse) {
            options.onAfterHandleResponse(banner);
        }
    }

    handleBannerClick() {
        if (this.banner instanceof BannerDataJson) {
            const json = this.banner;
            this.model.click(json.clickUrl);
        }
        if (this.banner && this.options.onBannerClick) {
            this.options.onBannerClick(this.banner);
        }
    }
}
